import { isBool, isObj } from 'lib/util'

export const DISABLED = 'disabled'
export const OPTIONAL = 'optional'
export const MANDATORY = 'mandatory'

/**
 * Whether the current attribute is a complex Object.
 *
 * @param {String} attribute  the attribute
 * @returns {function(*): *}
 */
export const isAttributeComplex = attribute => note => isObj(note[attribute])

/**
 * Whether the current attribute is required. Meaning either being `true` or
 * `MANDATORY`.
 *
 * @param {String} attribute  the attribute
 * @returns {function(*): *}
 */
export const isAttributeRequired = attribute => note => note[attribute] === true
  || note[attribute] === MANDATORY

/**
 * Whether the current attribute is visible. Meaning either required or optional.
 *
 * @param {String} attribute  the attribute
 * @returns {function(*): *}
 */
export const isAttributeVisible = attribute => note => isAttributeRequired(attribute)(note)
    || note[attribute] === OPTIONAL

/**
 * Returns `note`'s `mandatory` property.
 *
 * @param {Object} note the current node.
 * @returns {Object|Boolean}
 */
export const isNoteRequired = note => isBool(note.mandatory) && note.mandatory

/**
 * Return whether time tracking of `note` is required.
 * If the condition is a complex object, return it and let
 * the action logic handle it.
 *
 * @param {Object} note the current node.
 * @returns {Object|Boolean}
 */
export const isTimeTrackingRequired = note => note.timeTracking === MANDATORY

/**
 * Return whether time tracking of `note` is optional.
 * If the condition is a complex object, return it and let
 * the action logic handle it.
 *
 * @param {Object} note the current node.
 * @returns {Object|Boolean}
 */
export const isTimeTrackingOptional = note => note.timeTracking === OPTIONAL

/**
 * Return whether time tracking of `note` is visible.
 *
 * @param {Object} note the current node.
 * @returns {Object|Boolean}
 */
export const isTimeTrackingVisible = note => isTimeTrackingRequired(note)
    || isTimeTrackingOptional(note)

/**
 * Ticket Note Types Collection.
 *
 * Contains static list of available ticket's message types, which can be used for different
 * purposes, like filling lists, allowing options selection, etc.
 *
 * Please note: Every note type has a `mandatory` and `timeTracking` property
 * set up to configure their respective feature. However, they are not used with every
 * note type at the moment. For completeness’s sake (and possible future usage) however,
 * they are still present.
 *
 * mandatory:
 * - `true`: Note will be mandatory
 * - `false`: Note will be optional
 * - `Object`: Will return `onSuccess` property of the object if all properties inside
 * `rules` (each being a predicate) return true. Otherwise, will return `onFail`.
 * See ticket's {@link note} hook for more.
 *   @example
      mandatory: {
      onSuccess: OPTIONAL,
      onFail: DISABLED,
      rules: {
        status: [50],
        assignee: true,
      }
      },
 *
 * timeTracking:
 * - `DISABLED`: Will not be displayed
 * - `OPTIONAL`: Will be displayed, but not validated
 * - `MANDATORY`: Will be displayed and validated (with {@link isTimeEntry})
 * - `Object`: Same as above.
 */
const defaultNoteTypes = [
  // normal types
  {
    key: 'blank',
    value: 'Blank note',
    color: '',
    selectable: true,
    mandatory: false,
    timeTracking: OPTIONAL,
  },
  {
    key: 'processingStep',
    value: 'Processing Step',
    color: '',
    selectable: true,
    mandatory: false,
    timeTracking: OPTIONAL,
    startDate: DISABLED,
  },
  {
    key: 'birth',
    value: 'Birth note',
    color: 'primary',
    selectable: false,
    mandatory: false,
    timeTracking: DISABLED,
    startDate: DISABLED,
  },
  {
    key: 'hotline',
    value: 'Hotline',
    color: 'warning',
    mandatory: false,
    timeTracking: DISABLED,
    startDate: DISABLED,
  },
  // synthetic types
  {
    key: 'timeEntry',
    value: 'Time entry',
    color: '',
    selectable: false,
    mandatory: true,
    timeTracking: MANDATORY,
    startDate: MANDATORY,
  },
  // quick action types
  {
    key: 'forward',
    value: 'Forwarded to {team}',
    color: 'primary',
    mandatory: false,
    timeTracking: {
      onFail: DISABLED,
      onSuccess: OPTIONAL,
      rules: {
        status: [50],
        assignee: true, // I am assignee
      },
    },
    startDate: DISABLED,
  },
  {
    key: 'assign',
    value: 'Assigned to {assignee}',
    color: 'primary',
    mandatory: false,
    timeTracking: {
      onFail: DISABLED,
      onSuccess: OPTIONAL,
      rules: {
        status: [50],
        assignee: true, // I am assignee
      },
    },
    startDate: DISABLED,
  },
  {
    key: 'unassign',
    value: 'Unassigned from {assignee}',
    color: 'primary',
    mandatory: false,
    timeTracking: {
      onFail: DISABLED,
      onSuccess: OPTIONAL,
      rules: {
        status: [50],
        assignee: true, // I am assignee
      },
    },
    startDate: DISABLED,
  },
  {
    key: 'postpone',
    value: 'Postponed until {date}',
    color: 'warning',
    mandatory: false,
    timeTracking: OPTIONAL,
    startDate: DISABLED,
  },
  {
    key: 'postpone_customer',
    value: 'Waiting for customer until {date}',
    color: 'warning',
    mandatory: false,
    timeTracking: OPTIONAL,
    startDate: DISABLED,
  },
  {
    key: 'postpone_third_party',
    value: 'Waiting for third party until {date}',
    color: 'warning',
    mandatory: false,
    timeTracking: OPTIONAL,
    startDate: DISABLED,
  },
  {
    key: 'close_resolve',
    value: 'Issue resolved',
    color: 'success',
    mandatory: true,
    timeTracking: MANDATORY,
    startDate: DISABLED,
  },
  {
    key: 'close_cancel',
    value: 'Issue cancelled',
    color: 'success',
    mandatory: true,
    timeTracking: MANDATORY,
    startDate: DISABLED,
  },
  {
    key: 'reopen',
    value: 'Issue reopened',
    color: 'primary',
    mandatory: false,
    timeTracking: DISABLED,
    startDate: DISABLED,
  },
  {
    key: 'stop',
    value: 'Stop',
    color: 'disabled',
    mandatory: false,
    timeTracking: OPTIONAL,
    startDate: DISABLED,
  },
]

export const isTimeTrackingDisabled = noteTypes => (noteTypes || defaultNoteTypes).every(
  noteType => (noteType.timeTracking ? noteType.timeTracking === DISABLED : true)
    && (noteType.startDate ? noteType.startDate === DISABLED : true),
)

export default defaultNoteTypes
